import React from 'react'
import * as SwitchPrimitive from '@radix-ui/react-switch'
import { cn } from '../../utils/cn'
import { disabledFieldProps } from './input'

export interface SwitchProps extends Omit<
  React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>,
  'checked' | 'onChange'
> {
  isChecked?: boolean
  checked?: boolean
  isDisabled?: boolean
  colorScheme?: 'success' | 'warning' | 'default'
  /** Change handler receiving a synthetic event with target.checked. */
  onChange?: (event: { target: { checked: boolean } }) => void
}

const trackChecked: Record<string, string> = {
  default: 'data-[state=checked]:bg-primary',
  success: 'data-[state=checked]:bg-success',
  warning: 'data-[state=checked]:bg-warning',
}

/**
 * Switch (Radix) with `isChecked`/`isDisabled`/`onChange` props.
 */
export const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitive.Root>, SwitchProps>(
  (
    {
      className,
      isChecked,
      checked,
      isDisabled,
      disabled,
      colorScheme = 'default',
      onChange,
      onCheckedChange,
      ...props
    },
    ref,
  ) => (
    <SwitchPrimitive.Root
      ref={ref}
      checked={checked ?? isChecked}
      onCheckedChange={value => {
        onCheckedChange?.(value)
        onChange?.({ target: { checked: value } })
      }}
      className={cn(
        'peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent bg-line-emphasized transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 aria-disabled:cursor-not-allowed aria-disabled:pointer-events-none aria-disabled:opacity-50',
        trackChecked[colorScheme],
        className,
      )}
      {...props}
      // Disabled via JS/CSS (aria-disabled + guards), not the native attribute.
      {...disabledFieldProps(disabled ?? isDisabled)}
    >
      <SwitchPrimitive.Thumb className="pointer-events-none block h-4 w-4 rounded-full bg-surface-section shadow-sm ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0" />
    </SwitchPrimitive.Root>
  ),
)
Switch.displayName = 'Switch'
